import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import SelectHookFormMulti from "../../../../components/SelectHookFormMulti";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useGetLobTypeOptions } from "../hooks/useGetLobTypeOptions";
import { useGetProdCodeType } from "../hooks/useGetProdCodeType";
import { useGetMGA } from "../hooks/useGetMGA";
import { useStates } from "../../hooks/useStates";
import { useCarrierProgram } from "../../../Utilities/ProducerConfiguration/CarrierProgram/hooks/useCarrierProgram";
import { useGetProducerCodeListByCarrierId } from "../hooks/useGetProdCodeByCarrierId";
import { useGetBilling } from "../hooks/useGetBilling";
import ReactTooltip from "react-tooltip";
import {
  prodCodeAddState,
  prodCodeEditState,
  rowForEditProdCode,
} from "../atom";
import { useRecoilState, useRecoilValue } from "recoil";
import CheckboxForProducerCodeForm from "../../../../components/CheckboxForProducerCodeForm";
import { useEffect, useMemo } from "react";
import { accountAtom } from "../../../Login/atoms/AccountAtom";
import TextInputHookFormDisabled from "components/TextInputHookFormDisabled";
import { useGetCarrierNameList } from "../../Carrier/hooks/useGetCarrierNameList";

interface ProducerCodeLOBSectionProp {
  empFranchisesOpts?: any;
  confieEmpOpts?: any;
  employeeListLoading;
  franchiseType;
}

export const ProducerCodeLOBSection: React.FC<ProducerCodeLOBSectionProp> = ({
  empFranchisesOpts,
  confieEmpOpts,
  employeeListLoading,
  franchiseType,
}) => {
  const [isEditState] = useRecoilState(prodCodeEditState);
  const [isAddState] = useRecoilState(prodCodeAddState);
  const [rowForEdit] = useRecoilState(rowForEditProdCode);
  const user = useRecoilValue(accountAtom);
  const {
    control,
    formState: { errors },
    register,
    watch,
    setValue,
  } = useFormContext();

  // --- QUERY CARRIER STATUS ---
  const { data: carriersQuery } = useGetCarrierNameList();
  const selectedCarrierId = watch("carrierId");
  const selectedCarrierData = carriersQuery?.find(
    (c) => c.masterCarrierId === selectedCarrierId,
  );

  const getCarrierStatusLabel = () => {
    if (!selectedCarrierId) return "False"; // Default state when nothing is selected

    const isActive =
      selectedCarrierData?.isActive === true ||
      selectedCarrierData?.isActive === "Active";
    return isActive ? "Active" : "Inactive";
  };

  const statusLabel = getCarrierStatusLabel();

  const { data: allProducerCodeListByCarrier } =
    useGetProducerCodeListByCarrierId(watch("carrierId"));

  const masterCodesOpts = allProducerCodeListByCarrier?.map((x) => {
    return {
      label: x.producerCode + " - " + x.officeName,
      value: x.producerCode + " - " + x.officeName,
      isActive: x.isActive,
      isFranchise: x.isFranchise,
    };
  });

  const franchiseMasterCodesOpts = allProducerCodeListByCarrier
    ?.filter((y) => y.isFranchise === true)
    ?.map((x) => {
      return {
        label: x.producerCode + " - " + x.officeName,
        value: x.producerCode + " - " + x.officeName,
        isActive: x.isActive,
        isFranchise: x.isFranchise,
      };
    });

  const rollUpCodesOps = allProducerCodeListByCarrier?.map((x) => {
    return {
      label: x.producerCode + " - " + x.officeName,
      value: x.carrierProducerCodeId,
      isActive: x.isActive,
      isFranchise: x.isFranchise,
    };
  });

  const franchiseRollUpCodesOps = allProducerCodeListByCarrier
    ?.filter((y) => y.isFranchise === true)
    ?.map((x) => {
      return {
        label: x.producerCode + " - " + x.officeName,
        value: x.carrierProducerCodeId,
        isActive: x.isActive,
        isFranchise: x.isFranchise,
      };
    });

  const { data: LOBQuery, isFetching: lobLoading } = useGetLobTypeOptions();
  const lobOptions =
    LOBQuery &&
    LOBQuery.filter(
      (data) => data.isActive === true && data.lobname !== "",
    )?.map((resData) => ({
      label: resData.lobname,
      value: resData.lobid,
    }));

  const { data: prodTypeQuery, isFetching: prodTypeLoading } =
    useGetProdCodeType();

  const prodCodeTypeOptions =
    prodTypeQuery &&
    prodTypeQuery
      .filter((data) => data.isActive === true)
      .map((resData) => ({
        label: resData.producerCodeTypesName,
        value: resData.producerCodeTypesId,
      }));

  const { data: mga, isFetching: mgaLoading } = useGetMGA();
  const mgaOpt =
    mga &&
    mga
      .filter((data) => data.isActive === true)
      .map((x) => {
        return {
          label: x.mga1,
          value: x.id,
        };
      });

  const isFranchiseIndicator = watch("isFranchise");

  const issuedToOptions = useMemo(() => {
    return isFranchiseIndicator ? empFranchisesOpts : confieEmpOpts;
  }, [isFranchiseIndicator, empFranchisesOpts, confieEmpOpts]);

  const { data: states, isFetching: statesLoading } = useStates();

  const allStatesOpt = states?.map((x) => {
    return {
      label: x.state,
      value: x.state?.toString(),
    };
  });

  const { data: carrierProgram } = useCarrierProgram();
  const cProgramOpt = carrierProgram
    ?.filter((data) => data?.carrierId === watch("carrierId") && data?.isActive)
    ?.map((x) => ({
      value: x?.id,
      label: x?.programName,
    }));

  const { data: billing, isFetching: billingLoading } = useGetBilling();
  const billingOpt = billing?.map((x) => {
    return {
      value: x.billingId,
      label: x.billingName,
    };
  });

  const codeStatus = [
    { label: "Active", value: true },
    {
      label: "Inactive",
      value: false,
    },
  ];
  const statusOpt = [
    { label: "Active", value: true },
    {
      label: "Inactive",
      value: "false",
    },
    {
      label: "Suspended",
      value: 1,
    },
  ];

  const isFranchise = useWatch({
    control: control,
    name: "isFranchise",
    defaultValue: false,
  });

  const setExceptionBoxes = (val: boolean) => {
    setValue("isCDRCException", val);
    setValue("isKeeperException", val);
    setValue("isAL3Exception", val);
  };

  // Add
  useEffect(() => {
    if (!isAddState) return;

    setValue("masterCodeStatus", false, { shouldDirty: false });
    setValue("rollUpCodeStatus", false, { shouldDirty: false });
    setValue("status", true, { shouldDirty: false });
  }, [isAddState]);

  // Edit
  useEffect(() => {
    if (!isEditState || !rowForEdit) return;

    if (rowForEdit.isSuspended) {
      setValue("status", 1);
      return;
    }

    setValue(
      "status",
      rowForEdit.isActive === true ? rowForEdit.isActive : "false",
    );
  }, [isEditState, rowForEdit]);

  // Franchise Type cleanup
  useEffect(() => {
    if (!isEditState) return;
    if (isFranchise !== false) return;
  }, [isEditState, isFranchise]);

  return (
    <>
      <div className="pb-2">
        <div className="grid lg:grid-cols-3 md:grid-cols-3 sm:grid-cols-3 items-center w-full gap-y-2 gap-x-5">
          <Controller
            control={control}
            name="lobid"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectHookFormSingle
                label="Line of Business"
                value={field.value ?? null}
                field={field}
                options={lobOptions}
                onChange={(e) => {
                  field.onChange(e?.value ?? null);
                  setValue("lineofBusiness", e?.value);
                }}
                required={true}
                className={
                  errors.lobid && "border-2 border-red-500 col-span-2 "
                }
                secondDiv="grid grid-cols-3 items-center"
                isLoading={lobLoading}
              />
            )}
          />
          <Controller
            control={control}
            name="producerCodeTypeId"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectHookFormSingle
                label="Producer Code Type"
                value={field.value ?? null}
                field={field}
                options={prodCodeTypeOptions}
                onChange={(e) => {
                  field.onChange(e?.value ?? null);
                  setValue("producerCodeTypeId", e?.value ?? "");
                }}
                required={true}
                className={
                  errors.producerCodeTypeId &&
                  "border-2 border-red-500 col-span-2 "
                }
                secondDiv="grid grid-cols-3 w-full items-center gap-2"
                isLoading={prodTypeLoading}
              />
            )}
          />
          <div className="space-y-1 w-full text-xs">
            <div className="grid grid-cols-3 w-full items-center gap-2">
              {watch("isMa") ? (
                <label className="flex text-left expandedLabel fontExpand">
                  <span className="text-red-500 ml-1">*</span>M&A
                </label>
              ) : (
                <label className="flex text-left expandedLabel fontExpand">
                  M&A:
                </label>
              )}
              <div className="col-span-2 flex flex-row  gap-2">
                <ReactTooltip
                  id="isMA"
                  place="top"
                  effect="solid"
                  type="info"
                />

                <CheckboxForProducerCodeForm
                  name="isMa"
                  type="checkbox"
                  register={register}
                  required={watch("isMa")}
                  className=""
                />

                <div className="flex items-center">
                  <div className="relative flex items-center w-full">
                    {watch("isMa") && (
                      <TextInputLabelHookForm
                        name="maproducerCode"
                        register={register("maproducerCode", {
                          required: {
                            value: true,
                            message: "Required Field",
                          },
                        })}
                        type="text"
                        required={true}
                        placeholder="Producer Code"
                        className={
                          errors.maproducerCode &&
                          "border-2 border-red-500 col-span-2 "
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Controller
            control={control}
            name="issuedTo"
            render={({ field }) => (
              <SelectHookFormMulti
                label="Issued To"
                value={
                  field.value
                    ? field.value.filter((item) => item !== undefined)
                    : undefined
                }
                field={field}
                options={issuedToOptions}
                onChange={(e) => {
                  field.onChange(e);
                }}
                secondDiv="grid grid-cols-3 w-full items-center gap-2"
                isLoading={employeeListLoading}
              />
            )}
          />
          <Controller
            control={control}
            name="mga"
            render={({ field }) => (
              <SelectHookFormSingle
                label="MGA"
                value={field.value ?? null}
                field={field}
                options={mgaOpt}
                onChange={(e) => {
                  field.onChange(e?.value ?? null);
                }}
                secondDiv="grid grid-cols-3 w-full items-center gap-2"
                isLoading={mgaLoading}
              />
            )}
          />
          <Controller
            control={control}
            name="carrierProgram"
            render={({ field }) => (
              <SelectHookFormSingle
                label="Carrier Program Name"
                value={field.value ?? null}
                field={field}
                options={cProgramOpt}
                onChange={(e) => {
                  field.onChange(e?.value ?? null);
                }}
                isDisabled={!watch("carrierId")}
                secondDiv="grid grid-cols-3 w-full items-center gap-2"
              />
            )}
          />
          <Controller
            control={control}
            name="crossSale"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectHookFormMulti
                label="Can Sell"
                value={
                  field.value
                    ? field.value.filter((item) => item !== undefined)
                    : undefined
                }
                field={field}
                options={allStatesOpt}
                onChange={(e) => {
                  field.onChange(e);
                }}
                secondDiv="grid grid-cols-3 w-full items-center gap-2"
                isLoading={statesLoading}
                isRequired={true}
                className={
                  errors.crossSale && "border-2 border-red-500 col-span-2 "
                }
              />
            )}
          />

          <Controller
            control={control}
            name="status"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectHookFormSingle
                label="Producer Code Status"
                field={field}
                options={statusOpt}
                value={field?.value ?? "false"}
                isDisabled={isAddState}
                onChange={(e) => {
                  field.onChange(e?.value ?? "false");

                  if (e?.label === "Suspended") {
                    setValue("isSuspended", true);
                    setValue("isActive", false);
                  } else if (e?.label === "Active") {
                    setValue("isSuspended", false);
                    setValue("isActive", true);
                  } else if (e?.label === "Inactive") {
                    setValue("isSuspended", false);
                    setValue("isActive", false);
                  }

                  if (watch("carrierId") === 174 && e?.label === "Inactive") {
                    setExceptionBoxes(false);
                  } else if (
                    watch("carrierId") === 174 &&
                    e?.label !== "Inactive"
                  ) {
                    setExceptionBoxes(true);
                  }
                }}
                required={true}
                className={
                  errors.status && "border-2 border-red-500 col-span-2 "
                }
                secondDiv="grid grid-cols-3 w-full items-center gap-2"
              />
            )}
          />
          {user.isFranchise && watch("isFranchise") && (
            <Controller
              control={control}
              name="franchiseTypeId"
              render={({ field }) => (
                <SelectHookFormSingle
                  label="Franchise Type"
                  field={field}
                  options={franchiseType}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e?.value ?? null);
                  }}
                  secondDiv="grid grid-cols-3 w-full items-center gap-2"
                  isDisabled={watch("officeCode")
                    ?.toLowerCase()
                    .startsWith("z")}
                />
              )}
            />
          )}
          <Controller
            control={control}
            name="billingId"
            render={({ field }) => (
              <SelectHookFormSingle
                label="Billing"
                value={field.value ?? null}
                field={field}
                options={billingOpt}
                onChange={(e) => {
                  field.onChange(e?.value ?? null);
                }}
                secondDiv="grid grid-cols-3 w-full items-center gap-2"
                isLoading={billingLoading}
              />
            )}
          />
        </div>
        <div className="border-b border-gray-200 my-4"></div>
        <div className="grid lg:grid-cols-3 md:grid-cols-3 sm:grid-cols-3 items-center w-full gap-y-2 gap-x-5">
          <Controller
            control={control}
            name="masterCodeStatus"
            render={({ field }) => (
              <SelectHookFormSingle
                label="Master Code Status"
                value={field.value}
                field={field}
                options={codeStatus}
                onChange={(e) => {
                  field.onChange(e?.value ?? null);
                  if (e?.value === false) {
                    setValue("masterCode", null);
                  }
                }}
                isClearable={false}
                secondDiv="grid grid-cols-3  w-full items-center gap-2"
              />
            )}
          />
          <Controller
            control={control}
            name="rollUpCodeStatus"
            render={({ field }) => (
              <SelectHookFormSingle
                label="Roll Up Code Status"
                value={field.value}
                field={field}
                options={codeStatus}
                onChange={(e) => {
                  field.onChange(e?.value ?? null);

                  if (e?.value === false) {
                    setValue("rollUpId", null);
                  }
                }}
                isClearable={false}
                secondDiv="grid grid-cols-3 md:grid-cols-3 w-full items-center gap-2"
              />
            )}
          />
          <TextInputHookFormDisabled
            name="isInProduction"
            type="text"
            label="In Production"
            value={watch("isInProduction") ? "True" : "False"}
          />
        </div>
        <div className="grid lg:grid-cols-3 md:grid-cols-3 sm:grid-cols-3 items-center w-full gap-y-2 gap-x-5 pt-2">
          <Controller
            control={control}
            name="masterCode"
            rules={{ required: watch("masterCodeStatus") }}
            render={({ field }) => (
              <SelectHookFormSingle
                label="Master Code"
                value={field.value ?? null}
                field={field}
                options={
                  watch("isFranchise")
                    ? franchiseMasterCodesOpts
                    : masterCodesOpts
                }
                onChange={(e) => {
                  field.onChange(e?.value ?? null);
                }}
                isDisabled={!watch("carrierId") || !watch("masterCodeStatus")}
                secondDiv="grid grid-cols-3 md:grid-cols-3 w-full items-center gap-2"
                className={
                  errors.masterCode &&
                  watch("masterCodeStatus") &&
                  "border-2 border-red-500 col-span-2 "
                }
                required={watch("masterCodeStatus")}
              />
            )}
          />
          <Controller
            control={control}
            name="rollUpId"
            rules={{ required: watch("rollUpCodeStatus") }}
            render={({ field }) => (
              <SelectHookFormSingle
                label="Roll Up Code"
                value={field.value ?? null}
                field={field}
                options={
                  watch("isFranchise")
                    ? franchiseRollUpCodesOps
                    : rollUpCodesOps
                }
                onChange={(e) => {
                  field.onChange(e?.value ?? null);
                }}
                isDisabled={!(watch("carrierId") && watch("rollUpCodeStatus"))}
                secondDiv="grid grid-cols-3 md:grid-cols-3 w-full items-center gap-2"
                className={
                  errors.rollUpId &&
                  watch("rollUpCodeStatus") &&
                  "border-2 border-red-500 col-span-2 "
                }
                required={watch("rollUpCodeStatus")}
              />
            )}
          />
          <div className="grid grid-cols-3 items-center gap-2">
            <span className="text-xs flex items-stretch text-left expandedLabel fontExpand">
              Carrier Status:
            </span>

            <div className="col-span-2 text-xs ml-1">
              <span className="text-black-600">{statusLabel}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="border-b border-gray-200 mt-2 mb-4"></div>
    </>
  );
};
